import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner';
import { Button, Container, Spinner } from 'react-bootstrap'
import { useQueryClient } from '@tanstack/react-query';
import OccupancyTable from './occupancy/OccupancyTable'
import { useForm } from 'react-hook-form';
import YearSelectComponent from './occupancy/YearSelectComponent';
import DateRangePickerComponent from './occupancy/DateRangePickerComponentV2';
import RoomSelectComponent from './occupancy/RoomSelectComponent';
import PropertySelectComponent from './occupancy/PropertySelectComponent';
import { OccupancyMonthRoomType, OccupancyMonthRoomTypeSchema } from '../../../services/availability-and-rates/schema/OccupancyMonthRoomTypeSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import OccupancyTableYear from './occupancy/OccupancyTableYear';
import SkeletonTable from './occupancy/SkeletonTable';

import useOccupancyDaily from '../../../services/occupancy/hooks/useOccupancyDaily';
import useOccupancyMonth from '../../../services/occupancy/hooks/useOccupancyMonth';
import useOccupancyYearly from '../../../services/occupancy/hooks/useOccupancyYearly';
import useGetAllRoomType from '../../../services/room-type/hooks/useGetAllRoomType';

const OccupancyReportComponent: React.FC = () => {
    const queryClient = useQueryClient();
    const { control, handleSubmit, getValues, formState: { errors }, setValue, watch } = useForm<OccupancyMonthRoomType>({
        resolver: zodResolver(OccupancyMonthRoomTypeSchema),
        defaultValues: {
            filter: {
                property_id: "",
                start_date: "",
                end_date: "",
                room_type_id: "",
            },
        },
    });

    const [year, setYear] = useState<number | null>(null);
    const [reportType, setReportType] = useState<"monthly" | "daily" | null>(null);
    
    // Track applied params per report type for refresh logic
    const [appliedMonthlyParams, setAppliedMonthlyParams] = useState<{
        property_id: string;
        room_type_id: string;
        year: number;
    } | null>(null);

    const [appliedDailyParams, setAppliedDailyParams] = useState<{
        property_id: string;
        room_type_id: string;
        start_date: string;
        end_date: string;
    } | null>(null);
    const [filter, setFilter] = useState<OccupancyMonthRoomType["filter"]>({
        property_id: "",
        start_date: "",
        end_date: "",
        room_type_id: "",
    });

    // const { data: occupancyMonthRoomTypeData, isFetching: isFetchingOccupancyMonthRoomType } = useOccupancyMonthRoomType({
    //     filter: filter,
    //     type: reportType,

    // });

    // const { data: occupancyDailyRoomTypeData, isFetching: isFetchingOccupancyDailyRoomType } = useOccupancyDailyRoomType({
    //     filter: filter,
    //     type: reportType,
    // });

    const { data: allRoomTypesData } = useGetAllRoomType({
        property_id: filter.property_id,
        all: true, // Ambil semua data
    });

    const roomNamesMap = useMemo(() => {
        if (!allRoomTypesData?.data) return {};
        return allRoomTypesData.data.reduce((acc, roomType) => {
            acc[roomType.id] = roomType.name;
            return acc;
        }, {} as { [id: string]: string });
    }, [allRoomTypesData]);

    // A. Hooks untuk Laporan Harian
    const { data: dailyData, isFetching: isFetchingDaily } = useOccupancyDaily({
        filter: {
            start: filter.start_date,
            end: filter.end_date,
            property_id: filter.property_id,
            room_type_id: filter.room_type_id
        },
        enabled: reportType === 'daily' && !!filter.property_id
    });

    // B. Hooks untuk Laporan Bulanan (Rincian & Total)
    const { data: monthlyDataDetailed, isFetching: isFetchingMonthlyDetailed } = useOccupancyMonth({
        filter: {
            start: filter.start_date,
            end: filter.end_date,
            property_id: filter.property_id,
            room_type_id: filter.room_type_id
        },
        group_by: 'room_type',
        enabled: reportType === 'monthly' && !!filter.property_id
    });

    const { data: monthlyDataTotal, isFetching: isFetchingMonthlyTotal } = useOccupancyMonth({
        filter: {
            start: filter.start_date,
            end: filter.end_date,
            property_id: filter.property_id,
            room_type_id: filter.room_type_id
        },
        enabled: reportType === 'monthly' && !!filter.property_id
    });

    // C. Hooks untuk Laporan Tahunan (Rincian & Total) -> Digunakan untuk kolom TOTAL di tabel bulanan
    const { data: yearlyDataDetailed, isFetching: isFetchingYearlyDetailed } = useOccupancyYearly({
        filter: {
            start: filter.start_date,
            end: filter.end_date,
            property_id: filter.property_id,
            room_type_id: filter.room_type_id
        },
        group_by: 'room_type',
        enabled: reportType === 'monthly' && !!filter.property_id
    });

    const { data: yearlyDataTotal, isFetching: isFetchingYearlyTotal } = useOccupancyYearly({
        filter: {
            start: filter.start_date,
            end: filter.end_date,
            property_id: filter.property_id,
            room_type_id: filter.room_type_id
        },
        enabled: reportType === 'monthly' && !!filter.property_id
    });

    const pivotedData = useMemo(() => {
        if (!monthlyDataDetailed?.data) return {};
        const result: { [roomTypeId: string]: { [month: string]: string } } = {};
        monthlyDataDetailed.data.forEach(item => {
            if (!item.room_type_id) return;
            if (!result[item.room_type_id]) result[item.room_type_id] = {};
            const monthNumber = item.month.split('-')[1];
            result[item.room_type_id][monthNumber] = item.occupancy;
        });
        return result;
    }, [monthlyDataDetailed]);

    // Detect form changes for refresh button logic
    const currentFormValues = watch();

    const isMonthlyFormChanged = appliedMonthlyParams
        ? currentFormValues.filter?.property_id !== appliedMonthlyParams.property_id ||
          currentFormValues.filter?.room_type_id !== appliedMonthlyParams.room_type_id ||
          year !== appliedMonthlyParams.year
        : false;

    const isDailyFormChanged = appliedDailyParams
        ? currentFormValues.filter?.property_id !== appliedDailyParams.property_id ||
          currentFormValues.filter?.room_type_id !== appliedDailyParams.room_type_id ||
          currentFormValues.filter?.start_date !== appliedDailyParams.start_date ||
          currentFormValues.filter?.end_date !== appliedDailyParams.end_date
        : false;

    const isFetchingMonthlyReport = isFetchingMonthlyDetailed || isFetchingMonthlyTotal || isFetchingYearlyDetailed || isFetchingYearlyTotal;
    const isReadyToShowMonthly = !isFetchingMonthlyReport && reportType === 'monthly' && monthlyDataTotal !== undefined && yearlyDataDetailed !== undefined && yearlyDataTotal !== undefined;
    const isReadyToShowDaily = !isFetchingDaily && reportType === 'daily' && dailyData !== undefined;

    // Determine which button shows refresh
    const showRefreshMonthly = isReadyToShowMonthly && appliedMonthlyParams !== null && !isMonthlyFormChanged;
    const showRefreshDaily = isReadyToShowDaily && appliedDailyParams !== null && !isDailyFormChanged;

    // Fungsi tunggal untuk menangani submit berdasarkan tombol yang ditekan
    const onSubmit = useCallback(
        (data: OccupancyMonthRoomType, type: "monthly" | "daily") => {
            let updatedFilter = { ...data.filter };
            if (type === "monthly") {
                if (!year) {
                    console.error("Year must be selected!");
                    return;
                }
                updatedFilter.start_date = `${year}-01-01`;
                updatedFilter.end_date = `${year}-12-31`;

                // If refreshing, invalidate React Query cache
                if (showRefreshMonthly) {
                    queryClient.invalidateQueries({
                        queryKey: ['bookings', 'monthly', 'occupancy']
                    });
                    queryClient.invalidateQueries({
                        queryKey: ['bookings', 'yearly', 'occupancy']
                    });
                }

                setAppliedMonthlyParams({
                    property_id: data.filter.property_id,
                    room_type_id: data.filter.room_type_id || "",
                    year: year,
                });
            }

            if (type === "daily") {
                // If refreshing, invalidate React Query cache
                if (showRefreshDaily) {
                    queryClient.invalidateQueries({
                        queryKey: ['bookings', 'daily', 'calendar', 'occupancy', 'day']
                    });
                }

                setAppliedDailyParams({
                    property_id: data.filter.property_id,
                    room_type_id: data.filter.room_type_id || "",
                    start_date: data.filter.start_date,
                    end_date: data.filter.end_date,
                });
            }

            setReportType(type);
            setFilter(updatedFilter);
        },
        [year, showRefreshMonthly, showRefreshDaily, queryClient]
    );

    // Toast notifikasi saat data berhasil di-load
    useEffect(() => {
        if (isReadyToShowDaily) {
            toast.success('Occupancy daily report loaded successfully.');
        }
    }, [isReadyToShowDaily]);

    useEffect(() => {
        if (isReadyToShowMonthly) {
            toast.success('Occupancy monthly report loaded successfully.');
        }
    }, [isReadyToShowMonthly]);

    return (
        <Container fluid className="dashboard-content">
            <form onSubmit={handleSubmit((data) => onSubmit(data, "daily"))}>
                <div className="d-flex flex-column flex-md-row justify-content-between gap-4">
                    {/* Left Side: Property & Room Select */}
                    <div className="d-flex flex-column flex-md-row gap-3 w-100">
                        <div className="w-100">
                            <PropertySelectComponent setValue={setValue} />
                        </div>

                        <div className="w-100">
                            <RoomSelectComponent control={control} errors={errors} setValue={setValue} />
                        </div>
                    </div>

                    {/* Right Side: Year & Date Range + Buttons */}
                    <div className="d-flex flex-column flex-md-row gap-3 w-100">
                        <div className="d-flex flex-column gap-2 w-100">
                            <YearSelectComponent onChange={setYear} />
                            <Button
                                variant={showRefreshMonthly ? "info" : "warning"}
                                type="button"
                                onClick={() => onSubmit(getValues(), "monthly")}
                                disabled={isFetchingMonthlyReport}
                            >
                                {isFetchingMonthlyReport ? (
                                    <><Spinner animation="border" size="sm" className="me-2" />Loading...</>
                                ) : showRefreshMonthly ? (
                                    <><i className="bi bi-arrow-clockwise me-2"></i>Refresh Report</>
                                ) : (
                                    'Show Report'
                                )}
                            </Button>
                        </div>

                        <div className="d-flex flex-column gap-2 w-100">
                            <DateRangePickerComponent control={control} errors={errors} setValue={setValue} />
                            <Button 
                                variant={showRefreshDaily ? "info" : "warning"} 
                                type="submit" 
                                disabled={isFetchingDaily || isFetchingMonthlyReport}
                            >
                                {(isFetchingDaily || isFetchingMonthlyReport) ? (
                                    <><Spinner animation="border" size="sm" className="me-2" />Loading...</>
                                ) : showRefreshDaily ? (
                                    <><i className="bi bi-arrow-clockwise me-2"></i>Refresh Occupancy Daily Report</>
                                ) : (
                                    'Show Occupancy Daily Report'
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </form>
            {isFetchingDaily && <SkeletonTable columns={5} rows={10} />}
            {isFetchingMonthlyReport && <SkeletonTable columns={14} rows={5} />}

            {isReadyToShowDaily && <OccupancyTable data={dailyData} />}

            {isReadyToShowMonthly && (
                <OccupancyTableYear
                    pivotedData={pivotedData}
                    totalData={monthlyDataTotal.data}
                    yearlyDataDetailed={yearlyDataDetailed.data}
                    yearlyDataTotal={yearlyDataTotal.data}
                    roomNamesMap={roomNamesMap}
                />
            )}
        </Container>
    )
}

export default OccupancyReportComponent